import { cn, cva, platformProps } from "../../lib";
import { webRole } from "../../lib/web-role";
import { Box } from "../../primitives";
import { Icon } from "../icons";
import { Typography } from "../typography";
import {
  tagRibbonBackgroundClass,
  tagRibbonBorderClass,
  tagRibbonCornerClass,
  tagRibbonIconSize,
  tagRibbonTextColor,
  tagRibbonTypography,
} from "./lib";
import { TagRibbonTail } from "./tag-ribbon-tail";
import type { TagRibbonProps } from "./types";

// Figma's padding steps up on desktop (8 × 2 → 12 × 4) and is the same for both
// sizes; the size sets only the label and the icon. It sits 1px out from its
// parent's top corner, so on a card's 1px border it meets the outer corner,
// over the border (Figma `5882:3566`)
const tagRibbonVariants = cva(
  "gap-spacing-xs px-spacing-sm py-spacing-xxs md:px-spacing-md md:py-spacing-xs absolute z-10 inline-flex flex-row items-center",
  {
    variants: {
      position: {
        left: cn("-left-px -top-px", tagRibbonCornerClass.left),
        right: cn("-right-px -top-px", tagRibbonCornerClass.right),
      },
    },
    defaultVariants: {
      position: "left",
    },
  },
);

export const TagRibbon = ({
  children,
  state = "primary",
  emphasis = "solid",
  size = "md",
  position = "left",
  icon = false,
  className,
  accessibilityLabel,
  testID,
}: TagRibbonProps) => {
  const textColor = tagRibbonTextColor[emphasis][state];
  const label = accessibilityLabel || undefined;

  return (
    <Box
      // On web a label needs a group (ARIA gives a role-less div no name); on
      // native the ribbon becomes one element read by the label, in place of
      // its text. Without one, the visible text is read as-is
      accessibilityRole={label ? webRole("group", "text") : "text"}
      accessibilityLabel={label}
      accessible={label ? true : undefined}
      {...platformProps({ testID, slot: "tag-ribbon" })}
      className={cn(
        tagRibbonVariants({ position }),
        tagRibbonBackgroundClass[emphasis][state],
        className,
      )}
    >
      {/* Subtle's border sits inside the box, so both emphases are one size */}
      {emphasis === "subtle" && (
        <Box
          pointerEvents="none"
          className={cn(
            "absolute inset-0 border border-solid",
            tagRibbonCornerClass[position],
            tagRibbonBorderClass[state],
          )}
        />
      )}
      {icon && (
        <Icon name="StarFour" weight="fill" size={tagRibbonIconSize[size]} color={textColor} />
      )}
      <Typography variant={tagRibbonTypography[size]} color={textColor} numberOfLines={1}>
        {children}
      </Typography>
      <TagRibbonTail state={state} size={size} position={position} />
    </Box>
  );
};

TagRibbon.displayName = "TagRibbon";
